'use client'

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import EditIcon from '@mui/icons-material/Edit'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import { usePathname, useRouter } from 'next/navigation'

import { ArrowLeftIcon, ArrowRightIcon } from '@/src/components/ui/ArrowIcons'
import type { FlowStepConfig } from '@/src/lib/flow-engine/types'

interface SummaryStepProps {
  allSteps: FlowStepConfig[]
  savedFormData: Record<string, Record<string, unknown>>
  /** Pre-resolved display labels for field values, keyed by field key */
  fieldLabels: Record<string, string>
  onSubmit: () => Promise<void> | void
  onBack?: () => void
  isSubmitting?: boolean
}

export const SummaryStep: React.FC<SummaryStepProps> = ({
  allSteps,
  savedFormData,
  fieldLabels,
  onSubmit,
  onBack,
  isSubmitting = false
}) => {
  const { t, i18n } = useTranslation()
  const router = useRouter()
  const pathname = usePathname()
  const safePathname = pathname ?? ''
  const locale = safePathname.split('/')[1] || 'it'
  const [error, setError] = useState<string | null>(null)

  // Collect all steps that have fields + saved data
  const stepsWithData = allSteps.filter(
    (step) => step.fields.length > 0 && savedFormData[`step${step.id}`]
  )

  const summarySections = useMemo(() => {
    const sectionsByGroup = new Map<
      number,
      {
        groupId: number
        title: string
        editStepSlug: string
        items: Array<{ key: string; label: string; value: string }>
      }
    >()

    for (const step of stepsWithData) {
      const data = savedFormData[`step${step.id}`] ?? {}

      for (const field of step.fields) {
        if (field.type === 'hidden') continue

        const value = data[field.key]
        if (value === undefined || value === '' || value === null) continue

        let display: string
        if (typeof value === 'boolean') {
          display = value ? t('common.yes') : t('common.no')
        } else if (value instanceof Date) {
          display = value.toLocaleDateString('it')
        } else if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
          // Date string stored from date-picker-airbnb
          const parsed = new Date(value)
          display = !isNaN(parsed.getTime()) ? parsed.toLocaleDateString('it') : value
        } else if (fieldLabels[field.key]) {
          // Use pre-resolved label from the store (covers remote + static options)
          display = fieldLabels[field.key]
        } else if (field.options) {
          const opt = field.options.find((o) => o.value === String(value))
          display = opt ? t(opt.label, { defaultValue: opt.value }) : String(value)
        } else {
          display = String(value)
        }

        const existing = sectionsByGroup.get(step.group)
        const fallbackLabel =
          typeof field.label === 'string' && i18n.exists(field.label) ? t(field.label) : field.label
        const nextItem = {
          key: `${step.id}-${field.key}`,
          label: t(`fields.${field.key}.label`, { defaultValue: fallbackLabel }),
          value: display
        }

        if (existing) {
          existing.items.push(nextItem)
        } else {
          const groupTitle = step.summaryTitle
            ? t(step.summaryTitle)
            : t(`summary.groupTitles.${step.group}`, {
                defaultValue: t('summary.stepLabel', {
                  step: step.group,
                  defaultValue: `Step ${step.group}`
                })
              })
          sectionsByGroup.set(step.group, {
            groupId: step.group,
            title: groupTitle,
            editStepSlug: step.slug,
            items: [nextItem]
          })
        }
      }
    }

    return Array.from(sectionsByGroup.values()).sort((a, b) => a.groupId - b.groupId)
  }, [fieldLabels, savedFormData, stepsWithData, t])

  const defaultExpandedStepId = useMemo(
    () => summarySections.at(-1)?.groupId ?? null,
    [summarySections]
  )
  const [expandedStepId, setExpandedStepId] = useState<number | null>(defaultExpandedStepId)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    try {
      await onSubmit()
    } catch {
      setError(t('summary.submitError'))
    }
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%', maxWidth: 680 }}>
      <Typography variant="h3" sx={{ color: 'primary.main', mb: 1, textTransform: 'uppercase' }}>
        {t('summary.title')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        {t('summary.description')}
      </Typography>

      {/* Navigation */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 3,
          gap: 2
        }}
      >
        {onBack && (
          <Button
            variant="outlined"
            color="inherit"
            onClick={onBack}
            disabled={isSubmitting}
            sx={{
              color: 'text.secondary',
              borderColor: 'divider',
              justifyContent: 'space-between'
            }}
            startIcon={<ArrowLeftIcon />}
          >
            {t('common.back')}
          </Button>
        )}

        <Button
          type="submit"
          variant="contained"
          color="success"
          disabled={isSubmitting}
          sx={{
            width: 206,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 4
          }}
          endIcon={<ArrowRightIcon />}
        >
          <span>{t('common.submit')}</span>
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {summarySections.map((section) => {
        return (
          <Accordion
            key={section.groupId}
            expanded={expandedStepId === section.groupId}
            onChange={(_, expanded) => setExpandedStepId(expanded ? section.groupId : null)}
            disableGutters
            sx={{
              mb: 2,
              boxShadow: 'none',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '12px !important',
              overflow: 'hidden',
              '&:before': { display: 'none' }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                px: 3,
                py: 0.5,
                flexDirection: 'row-reverse',
                '& .MuiAccordionSummary-expandIconWrapper': {
                  mr: 1
                },
                bgcolor: expandedStepId === section.groupId ? '#f8f8fa' : 'background.paper',
                '& .MuiAccordionSummary-content': {
                  my: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%'
                }
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontFamily: '"Barlow Condensed", sans-serif',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  color: 'primary.main'
                }}
              >
                {section.title}
              </Typography>
              <Box
                component="span"
                role="link"
                tabIndex={0}
                onClick={(event: React.MouseEvent) => {
                  event.stopPropagation()
                  const basePath = safePathname.includes('/booking-worker')
                    ? `/${locale}/booking-worker`
                    : `/${locale}/booking-student`
                  router.push(`${basePath}/${section.editStepSlug}/`)
                }}
                onKeyDown={(event: React.KeyboardEvent) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.stopPropagation()
                    event.preventDefault()
                    const basePath = safePathname.includes('/booking-worker')
                      ? `/${locale}/booking-worker`
                      : `/${locale}/booking-student`
                    router.push(`${basePath}/${section.editStepSlug}/`)
                  }
                }}
                sx={{
                  ml: 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  textTransform: 'uppercase',
                  color: 'primary.main',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                <EditIcon sx={{ fontSize: '1rem' }} />
                {t('summary.editLink', { defaultValue: 'Modifica' })}
              </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ px: 3, pb: 2, pt: 1.5 }}>
              <Grid container spacing={2}>
                {section.items.map((item) => {
                  return (
                    <Grid key={item.key} size={{ xs: 12, md: 6 }}>
                      <Box
                        sx={{
                          py: 0.75,
                          minHeight: '100%'
                        }}
                      >
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          {item.label}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {item.value}
                        </Typography>
                      </Box>
                    </Grid>
                  )
                })}
              </Grid>
            </AccordionDetails>
          </Accordion>
        )
      })}
    </Box>
  )
}
